import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { SurveyService } from '../../../service/survey.service';
import { MessageService } from 'src/app/service/message.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SurveyNewProtectionGiuRequest } from 'src/app/model/survey-request';
import { SurveyNeedsGiuResponse } from 'src/app/model/survey-response';
import { decodeFlag, encodeFlag } from 'src/app/utils/common.functions';
import { fiscalCodeValidator, notEmptyValidator } from 'src/app/utils/validators';
import { SurveyNeedsEmit } from 'src/app/model/survey-emits';
import { FadeInOut } from 'src/app/utils/animation';

@Component({
  selector: 'needs-giu',
  templateUrl: './needs-giu.component.html',
  animations: [FadeInOut()]
})
export class NeedsGiuComponent implements OnChanges {
  @Input() surveyId!: string;
  @Input() needs?: SurveyNeedsGiuResponse;
  @Output() complete = new EventEmitter<SurveyNeedsEmit>();

  formGroup!: FormGroup;
  loading: boolean = false;
  completed: boolean = false;

  constructor(
    private surveyService: SurveyService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the surveyId (new survey) or needs (show survey) inputs have changed
    if((changes['surveyId'] && changes['surveyId'].firstChange) || changes['needs']) {
      this.init();
    }
  }

  init(): void {
    this.formGroup = new FormGroup({
      coverageNeeds: new FormControl(this.needs?.coverageNeeds || []),
      businessType: new FormControl(this.needs?.businessType || [], notEmptyValidator()),
      employeesCoverage: new FormControl(this.needs?.employeesCoverage || []),
      coverageProposal: new FormControl(encodeFlag(this.needs?.coverageProposal), Validators.required),
      legalRepresentative: new FormControl(encodeFlag(this.needs?.legalRepresentative), Validators.required),
      legalRepresentativeName: new FormControl(this.needs?.legalRepresentativeName, Validators.required),
      legalRepresentativeSurname: new FormControl(this.needs?.legalRepresentativeSurname, Validators.required),
      legalRepresentativeFiscalCode: new FormControl(this.needs?.legalRepresentativeFiscalCode, [Validators.required, fiscalCodeValidator()]),
      premium: new FormControl(this.needs?.premium || [])
    });

    this.coverageNeeds?.valueChanges.subscribe({
      next: (value: string[]) => {
        if(value.includes('A') || value.includes('B')) {
          this.legalRepresentative?.enable();
          this.premium?.enable();
        } else {
          this.legalRepresentative?.disable();
          this.legalRepresentative?.reset();
          this.premium?.disable();
          this.premium?.patchValue([]);
        }

        if(value.includes('C')) {
          this.employeesCoverage?.enable();
        } else {
          this.employeesCoverage?.disable();
          this.employeesCoverage?.patchValue([]);
        }
      }
    });

    this.legalRepresentative?.valueChanges.subscribe({
      next: (value: string) => {
        if(value === 'N') {
          this.legalRepresentativeName?.enable();
          this.legalRepresentativeSurname?.enable();
          this.legalRepresentativeFiscalCode?.enable();
        } else {
          this.legalRepresentativeName?.disable();
          this.legalRepresentativeSurname?.disable();
          this.legalRepresentativeFiscalCode?.disable();
          this.legalRepresentativeName?.reset();
          this.legalRepresentativeSurname?.reset();
          this.legalRepresentativeFiscalCode?.reset();
        }
      }
    });

    this.legalRepresentative?.disable();
    this.legalRepresentativeName?.disable();
    this.legalRepresentativeSurname?.disable();
    this.legalRepresentativeFiscalCode?.disable();
    this.premium?.disable();

    this.employeesCoverage?.disable();

    if(!this.isInsert()) {
      this.completed = true;
      this.formGroup.disable();
    }
  }

  isInsert(): boolean {
    return this.needs === undefined;
  }

  get coverageNeeds() { return this.formGroup.get('coverageNeeds') }
  get businessType() { return this.formGroup.get('businessType') }
  get employeesCoverage() { return this.formGroup.get('employeesCoverage') }
  get coverageProposal() { return this.formGroup.get('coverageProposal') }
  get legalRepresentative() { return this.formGroup.get('legalRepresentative') }
  get legalRepresentativeName() { return this.formGroup.get('legalRepresentativeName') }
  get legalRepresentativeSurname() { return this.formGroup.get('legalRepresentativeSurname') }
  get legalRepresentativeFiscalCode() { return this.formGroup.get('legalRepresentativeFiscalCode') }
  get premium() { return this.formGroup.get('premium') }

  onCheckChange(field: AbstractControl | null, event: MatCheckboxChange) {
    const values = field?.value || [];

    if(event.checked) {
      values.push(event.source.value);
    } else {
      const index = field?.value.indexOf(event.source.value, 0);
      if (index > -1) {
        values.splice(index, 1);
      }
    }

    // Need to use patchValue to trigger the valueChanges event
    field?.patchValue(values);
  }

  hasError(field: AbstractControl | null, errorType: string | undefined = undefined) {
    let hasError = !!field && field.touched && !field.disabled && !field.valid;

    if(hasError && !!errorType) {
      return field?.getError(errorType);
    }

    return hasError;
  }

  onSubmit() {
    if(!this.formGroup.valid || this.completed) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.formGroup.disable();
    this.completed = true;
    this.loading = true;

    const payload: SurveyNewProtectionGiuRequest = {
      coverageNeeds: this.coverageNeeds?.value,
      businessType: this.businessType?.value,
      employeesCoverage: this.employeesCoverage?.value,
      coverageProposal: decodeFlag(this.coverageProposal?.value),
      legalRepresentative: decodeFlag(this.legalRepresentative?.value),
      legalRepresentativeName: this.legalRepresentativeName?.value,
      legalRepresentativeSurname: this.legalRepresentativeSurname?.value,
      legalRepresentativeFiscalCode: this.legalRepresentativeFiscalCode?.value,
      premium: this.premium?.value
    }

    this.surveyService.insertNeedsGiu(this.surveyId, payload).subscribe({
      next: () => {
        this.loading = false;
        this.complete.emit({
          previdence: false,
          savings: this.coverageNeeds?.value.includes('A') || this.coverageNeeds?.value.includes('B'),
          savingsInvestment: this.premium?.value.includes('A'),
          savingsPlan: this.premium?.value.includes('B'),
          protection: false
        });
      },
      error: () => {
        this.messageService.error('survey.new.message.unknown-error');

        this.loading = false;
        this.completed = false;
        this.formGroup.enable();
      }
    });
  }
}
